import {
  collection,
  type FirestoreDataConverter,
  getFirestore,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore'
import { z } from 'zod'

import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

const ChangedByZod = z.object({
  uid: z.string(),
  username: z.string(),
})

const BaseConfigurationHistoryEventZod = z.object({
  timestamp: z.instanceof(Timestamp).transform((v) => v.toDate()),
  changedBy: ChangedByZod,
  eveningId: z.number().optional(),
  operatingDate: z.string().optional(),
})

const AssignmentHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('assignment'),
  previousValue: z.enum(['manual', 'trf', 'solver', 'legacy']),
  newValue: z.enum(['manual', 'trf', 'solver', 'legacy']),
})

const MobileRequestsHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('mobileRequests'),
  previousValue: z.boolean(),
  newValue: z.boolean(),
  source: z.enum(['central', 'evening']).optional(),
})

const WaitTimeHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('waitTime'),
  previousValue: z.enum(['default', 'increased']),
  newValue: z.enum(['default', 'increased']),
})

const AssistedAssignmentHistoryEventZod = BaseConfigurationHistoryEventZod.extend({
  type: z.literal('assistedAssignment'),
  previousValue: z.boolean(),
  newValue: z.boolean(),
})

const ConfigurationHistoryEventZod = z.discriminatedUnion('type', [
  AssignmentHistoryEventZod,
  MobileRequestsHistoryEventZod,
  WaitTimeHistoryEventZod,
  AssistedAssignmentHistoryEventZod,
])

export type ConfigurationHistoryEvent = z.infer<typeof ConfigurationHistoryEventZod> & { _id: string }

const ConfigurationHistoryConverter: FirestoreDataConverter<ConfigurationHistoryEvent> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot, options) => {
    const result = ConfigurationHistoryEventZod.safeParse(snapshot.data(options))
    if (!result.success) {
      throw new ErrorWithSentryCaptureContext(
        `Invalid configuration history event ${snapshot.id}`,
        {
          tags: { 'firestore.zod': 'configurationHistory' },
          fingerprint: ['firestore', 'zod', 'configurationHistory'],
          extra: { data: snapshot.data(options), document: { path: snapshot.ref.path } },
        },
        { cause: result.error }
      )
    }
    return { _id: snapshot.id, ...result.data }
  },
}

export const configurationHistoryCollection = (regionId: string) =>
  collection(getFirestore(), 'regions', regionId, 'configurationHistory').withConverter(ConfigurationHistoryConverter)

export const configurationHistoryQuery = (regionId: string, operatingDate?: string) => {
  const baseCollection = configurationHistoryCollection(regionId)

  if (operatingDate) {
    // Requires composite index: (operatingDate ASC, timestamp ASC)
    return query(baseCollection, where('operatingDate', '==', operatingDate), orderBy('timestamp', 'asc'))
  }

  return query(baseCollection, orderBy('timestamp', 'asc'))
}
